"use client";

import { useState } from "react";
import { ArrowRight, Loader2, Mail } from "lucide-react";
import { cn } from "@/lib/utils";
import { TurnstileWidget } from "./TurnstileWidget";

type EmailCaptureProps = {
  headline?: string;
  description?: string;
  ctaText?: string;
  className?: string;
  variant?: "card" | "inline";
  procedureSlug?: string;
  citySlug?: string;
  cityName?: string;
  /** Called after a successful capture (e.g. to set a cookie) */
  onSuccess?: () => void;
};

export function EmailCapture({
  headline = "Get pricing updates for this guide",
  description,
  ctaText = "Email Me Updates",
  className,
  variant = "card",
  procedureSlug,
  citySlug,
  cityName: _cityName,
  onSuccess,
}: EmailCaptureProps) {
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [turnstileResetKey, setTurnstileResetKey] = useState(0);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!consent) {
      setError("Please tick the consent box to continue.");
      return;
    }
    if (!turnstileToken) {
      setError("Complete the verification challenge to continue.");
      return;
    }
    // Honeypot: silently "succeed" if filled
    if (honeypot) {
      setSubmitted(true);
      return;
    }
    setError("");
    setIsPending(true);

    try {
      const res = await fetch("/api/email-capture", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          procedureSlug,
          citySlug,
          pageUrl: window.location.href,
          turnstileToken,
          consent,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error ?? "Something went wrong. Please try again.");
        return;
      }

      setSubmitted(true);
      onSuccess?.();
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setTurnstileToken("");
      setTurnstileResetKey((current) => current + 1);
      setIsPending(false);
    }
  }

  if (submitted) {
    return (
      <div
        className={cn(
          "rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center",
          variant === "card" && "shadow-sm",
          className,
        )}
      >
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-md bg-emerald-700 text-white">
          <Mail size={20} />
        </div>
        <p className="text-lg font-bold text-emerald-900">You are on the list</p>
        <p className="mt-1 text-sm text-emerald-700">
          We'll send you pricing updates and guide refreshes when they are ready.
        </p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "rounded-2xl border border-emerald-500/20 bg-white p-6",
        variant === "card" && "shadow-sm",
        className,
      )}
    >
      <p className="text-lg font-semibold tracking-tight text-slate-900">{headline}</p>
      {description && (
        <p className="mt-1 text-sm text-slate-500">{description}</p>
      )}

      <form onSubmit={handleSubmit} className="mt-4 flex gap-2">
        {/* Honeypot; aria-hidden on input to prevent aria-hidden-focus violation */}
        <div className="absolute -left-[9999px]" aria-hidden="true">
          <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
        </div>
        <div className="relative flex-1">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <label htmlFor="email-capture-email" className="sr-only">Email address</label>
          <input
            id="email-capture-email"
            type="email"
            required
            placeholder="you@example.com"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setError(""); }}
            aria-invalid={!!error}
            aria-describedby={error ? "email-capture-error" : undefined}
            className="h-11 w-full rounded-xl border border-black/10 bg-slate-50 pl-9 pr-3 text-sm font-medium text-slate-900 placeholder:text-slate-500 outline-none focus:border-emerald-600/40 focus:ring-2 focus:ring-emerald-600/10 transition-colors"
          />
        </div>
        <button
          type="submit"
          disabled={isPending || !consent || !turnstileToken}
          className="inline-flex h-11 shrink-0 items-center gap-2 rounded-xl bg-emerald-700 px-5 text-sm font-bold text-white shadow-sm hover:bg-emerald-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isPending ? (
            <>
              Sending...
              <Loader2 size={14} className="animate-spin" />
            </>
          ) : (
            <>
              {ctaText}
              <ArrowRight size={14} />
            </>
          )}
        </button>
      </form>

      {error && (
        <p id="email-capture-error" role="alert" className="mt-2 text-sm font-medium text-red-600">
          {error}
        </p>
      )}
      <TurnstileWidget
        action="email-capture"
        resetKey={turnstileResetKey}
        onTokenChange={setTurnstileToken}
        className="mt-3"
      />
      <label htmlFor="email-capture-consent" className="mt-3 flex items-start gap-2 text-xs text-slate-500">
        <input
          id="email-capture-consent"
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-0.5 h-3.5 w-3.5 shrink-0"
          required
        />
        <span>
          Email me updates on medical costs in Australia. I have read the{" "}
          <a href="/privacy" className="underline hover:text-slate-700">privacy policy</a>{" "}
          and can unsubscribe any time.
        </span>
      </label>
    </div>
  );
}
